/**
 * Client-side image compression helpers — **PNG only**.
 *
 * We ask the browser to decode the file, draw it onto a canvas that has
 * been scaled to fit inside ``maxDim`` on its longest edge, then re-encode
 * it as PNG. The compressed result is returned as a Data URL so callers
 * can save it directly to Mongo — no backend changes needed.
 *
 * Typical use:
 *
 *     const dataUrl = await compressImageToDataURL(file, { maxDim: 512 });
 *     await api.put("/profile", { avatar_url: dataUrl });
 *
 * If anything fails (unsupported codec, ancient browser), the helper falls
 * back to the raw ``readAsDataURL`` output so uploads always succeed.
 */

const DEFAULTS = { maxDim: 512 };

/** Read a File into an HTMLImageElement (respects EXIF orientation on
 *  modern browsers via ``createImageBitmap``). */
async function loadImage(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch { /* fall through to <img> */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * Validate that a file is a PNG image.
 * @param {File} file
 * @returns {{ valid: boolean, message?: string }}
 */
export function validatePngFile(file) {
  if (!file) return { valid: false, message: "No file selected." };
  const isPng =
    file.type === "image/png" ||
    (file.name && file.name.toLowerCase().endsWith(".png"));
  if (!isPng) {
    return {
      valid: false,
      message: "Only PNG images are allowed. Please select a .png file.",
    };
  }
  return { valid: true };
}

/**
 * Compress a PNG image File to a Data URL fitting ``maxDim`` on its longest
 * edge. Returns the compressed PNG string; falls back to raw base64 on any
 * decode / encode failure so uploads never break.
 */
export async function compressImageToDataURL(file, opts = {}) {
  if (!file) return "";
  const { maxDim } = { ...DEFAULTS, ...opts };
  try {
    const bmp = await loadImage(file);
    const width = bmp.width || bmp.naturalWidth;
    const height = bmp.height || bmp.naturalHeight;
    if (!width || !height) return readAsDataURL(file);
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return readAsDataURL(file);
    // Transparent background for PNG
    ctx.clearRect(0, 0, targetW, targetH);
    ctx.drawImage(bmp, 0, 0, targetW, targetH);
    if (typeof bmp.close === "function") bmp.close();
    const dataUrl = canvas.toDataURL("image/png");
    return dataUrl;
  } catch {
    return readAsDataURL(file);
  }
}

export default compressImageToDataURL;
